import {
  Color,
  DoubleSide,
  type Group,
  Mesh,
  MeshBasicMaterial,
  type Object3D,
  OctahedronGeometry,
  RingGeometry,
  Vector3,
} from 'three';
import type { SoundPlacement } from '../audio/soundscape';
import { spatialFalloff } from '../audio/audioManager';
import { devBus, focusMatches, type FocusRef } from './devBus';
import { CHANNEL_COLORS } from './audioChannelColors';

// Audio source gizmos — per authored spatial source, a small diamond MARKER at
// the source point + two thin horizontal RINGS for the core (refDistance) and
// reach (maxDistance) radii. Channel-colored, dim when silent, brighter when
// playing, and popping on a cross-surface audio:focus.
//
// Why rings, not spheres: in a small room the camera sits inside almost every
// source's radius, and a wireframe SPHERE then fills the centre of the view
// with its far hemisphere (a "blob stuck to the camera"). Flat rings lie in the
// source's horizontal plane, so they show location + reach WITHOUT ever
// occluding the middle of the view — clear sightlines whether you're inside the
// radius or looking from across the room.
//
// PLACEMENT-driven: every authored spatial bed + the diegetic music slot shows
// its marker + rings even when SILENT or off-era (dim), so it's a persistent map
// of where each hero's sound lives and how far it carries. Radii come from
// spatialFalloff() so tuning a source's reach in the bed editor moves the rings
// live. 'global' placements have no position/falloff, so they get no gizmo.
//
// Gizmos opt out of raycast + hover + wallCull via userData and never write
// depth, so they overlay the scene without occluding or being pickable — the
// camera + selection raycasts ignore them entirely.

const FOCUS_COLOR = 0x66ffe6;
// A2 — a placement whose authored hero anchor doesn't resolve used to draw
// NOTHING (the viz hid what the ear would have heard as a global loop). Now the
// engine SKIPS such a placement; the gizmo shows a distinct warning marker at
// the origin (no rings — there's no real position) so "anchored to a missing
// hero" is visible rather than invisible.
const UNRESOLVED_COLOR = 0xff3b3b;
const UNRESOLVED_POS = { x: 0, y: 1, z: 0 }; // a visible fallback spot (no real anchor)
const OPT_OUT = ['no_cull', 'raycastIgnore', 'no_outline', 'no_highlight'];
const MARKER_SIZE = 0.12; // metres — a small source point, not a view-filling shape

interface SourceVisual {
  marker: Mesh;
  ringInner: Mesh;
  ringOuter: Mesh;
}

export interface AudioGizmoDeps {
  /** Live authored placements (beds + music) — read each frame so edits show. */
  getPlacements: () => SoundPlacement[];
  /** Resolve a hero anchor to its scene object (for the source world position). */
  getObjectByHeroId: (id: string) => Object3D | undefined;
  /** Live gain of a placement if it's sounding, else null (→ dim). */
  activeGain: (placementId: string) => number | null;
}

export class AudioGizmos {
  private group: Group;
  private deps: AudioGizmoDeps;
  private sources = new Map<string, SourceVisual>();
  private unsubs: Array<() => void> = [];
  private scratch = new Vector3();
  private focusRef: FocusRef | null = null;
  // Shared unit geometries (each mesh scales to its real size/radius, so a live
  // reach edit is just a scale change — no geometry churn). The ring is a thin
  // flat annulus (lies in XZ once rotated); the marker a tiny diamond.
  private gMarker = new OctahedronGeometry(1, 0);
  private gRing = new RingGeometry(0.93, 1, 40);

  constructor(parent: Group, deps: AudioGizmoDeps) {
    this.group = parent;
    this.deps = deps;
    this.unsubs.push(devBus.on('audio:focus', (p) => { this.focusRef = p.ref; }));
  }

  /** Per-frame: reconcile + position every source's marker + rings. (No camera
   *  dependency — rings are world-anchored and the listener is the camera.) */
  update(): void {
    if (!this.group.visible) return;

    const seen = new Set<string>();
    for (const p of this.deps.getPlacements()) {
      const fall = spatialFalloff(p.spatial, p.refDistance, p.maxDistance);
      if (!fall) continue; // global / non-spatial → no gizmo

      // Resolve the source's world position (hero anchor, else fixed position).
      let hasPos = false;
      let unresolved = false;
      if (p.at?.heroId) {
        const obj = this.deps.getObjectByHeroId(p.at.heroId);
        if (obj) { obj.getWorldPosition(this.scratch); hasPos = true; }
        else { unresolved = true; this.scratch.set(UNRESOLVED_POS.x, UNRESOLVED_POS.y, UNRESOLVED_POS.z); hasPos = true; }
      } else if (p.at?.position) {
        this.scratch.set(p.at.position[0], p.at.position[1], p.at.position[2]);
        hasPos = true;
      }
      if (!hasPos) continue; // spatial but no anchor authored → no phantom gizmo

      seen.add(p.id);
      const v = this.ensure(p.id);
      // A2: an unresolved anchor draws only the warning marker — no rings, since
      // there's no real position to show a falloff around.
      v.marker.position.copy(this.scratch);
      v.marker.visible = true;
      for (const ring of [v.ringInner, v.ringOuter]) {
        ring.position.copy(this.scratch);
        ring.visible = !unresolved;
      }
      v.ringInner.scale.set(fall.refDistance, fall.refDistance, fall.refDistance);
      v.ringOuter.scale.set(fall.maxDistance, fall.maxDistance, fall.maxDistance);
      if (unresolved) {
        const mm = v.marker.material as MeshBasicMaterial;
        mm.color.set(UNRESOLVED_COLOR);
        mm.opacity = 0.9;
        continue; // skip the channel-color/brightness pass below
      }

      // Brightness: dim when silent, brighter (× live gain) when audible, with a
      // strong pop when this source is focused from any surface.
      const playing = this.deps.activeGain(p.id);
      const g = playing == null ? 0 : Math.min(1, playing);
      const live = playing != null;
      const focused = focusMatches(this.focusRef, { placementId: p.id, channel: p.channel, heroId: p.at?.heroId, assetId: p.asset });
      const color = focused ? FOCUS_COLOR : (CHANNEL_COLORS[p.channel] ?? 0xffffff);
      const mm = v.marker.material as MeshBasicMaterial;
      const im = v.ringInner.material as MeshBasicMaterial;
      const om = v.ringOuter.material as MeshBasicMaterial;
      mm.opacity = focused ? 0.95 : (live ? 0.55 + 0.4 * g : 0.4);
      im.opacity = focused ? 0.8 : (live ? 0.35 + 0.4 * g : 0.22);
      om.opacity = focused ? 0.4 : (live ? 0.14 + 0.16 * g : 0.1);
      mm.color.set(color); im.color.set(color); om.color.set(color);
    }

    // Prune visuals whose placement was deleted.
    for (const [id, v] of [...this.sources]) {
      if (!seen.has(id)) this.removeVisual(id, v);
    }
  }

  dispose(): void {
    for (const fn of this.unsubs) fn();
    this.unsubs = [];
    for (const [id, v] of [...this.sources]) this.removeVisual(id, v);
    this.gMarker.dispose();
    this.gRing.dispose();
  }

  // ── private ────────────────────────────────────────────────────────────
  private ensure(id: string): SourceVisual {
    const existing = this.sources.get(id);
    if (existing) return existing;
    const mkMesh = (geom: OctahedronGeometry | RingGeometry, opacity: number, ring: boolean): Mesh => {
      const mat = new MeshBasicMaterial({ color: new Color(0xffffff), transparent: true, opacity, depthWrite: false, side: DoubleSide });
      const mesh = new Mesh(geom, mat);
      if (ring) mesh.rotation.x = -Math.PI / 2; // lie flat in the source's XZ plane
      for (const k of OPT_OUT) mesh.userData[k] = true;
      mesh.renderOrder = 990;
      this.group.add(mesh);
      return mesh;
    };
    const marker = mkMesh(this.gMarker, 0.4, false);
    marker.scale.setScalar(MARKER_SIZE);
    const v: SourceVisual = { marker, ringInner: mkMesh(this.gRing, 0.22, true), ringOuter: mkMesh(this.gRing, 0.1, true) };
    this.sources.set(id, v);
    return v;
  }

  private removeVisual(id: string, v: SourceVisual): void {
    for (const mesh of [v.marker, v.ringInner, v.ringOuter]) {
      this.group.remove(mesh);
      (mesh.material as MeshBasicMaterial).dispose();
    }
    this.sources.delete(id);
  }
}
